import { IPost } from '@sdk/contracts';
import { Empty } from 'antd';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CreatePost } from './CreatePost';
import { PostForm } from './PostForm';
import { PostItem } from './PostItem';
import { Wrapper } from './PostWidget.styles';

import { LoadingCircleSpinner } from '@/components/Loaders';
import { PostViewer } from '@/components/PostViewer';
import { WidgetBase } from '@/components/WidgetBase';
import { usePostsList, useProfile } from '@/store';
import { WidgetProps } from '@/types';

type Props = {
  editable?: boolean;
} & WidgetProps;

const LOADER_SIZE = 40;

export const PostWidget: React.FC<Props> = ({ editable, ...props }) => {
  const { posts = [], isPending, loadMorePosts } = usePostsList();
  const { t } = useTranslation('CommonDashboardTranslations');
  const listRef = useRef<HTMLDivElement>(null);
  const { roleCheck } = useProfile();

  const { isAdmin, isManager, isTeacher } = roleCheck;
  const isEditable = isAdmin || isManager || isTeacher;

  const onScroll = useCallback(() => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10 && !isPending) {
        loadMorePosts();
      }
    }
  }, [isPending, loadMorePosts]);

  const shouldBeSetAsFeatured = useCallback((post: IPost, index: number) => {
    const isFirstOrSecond = index === 0 || index === 1;
    return isFirstOrSecond && post.isFeatured;
  }, []);

  return (
    <>
      <WidgetBase title={t('NEWS')} addon={<CreatePost editable={editable} />} {...props}>
        <Wrapper ref={listRef} onScroll={onScroll}>
          {posts.length === 0 && <Empty description={t('NO_NEWS_TO_SHOW')} />}
          {posts.map((post, index) => (
            <PostItem key={post._id} post={post} isFeatured={shouldBeSetAsFeatured(post, index)} />
          ))}
          {isPending && <LoadingCircleSpinner size={LOADER_SIZE} />}
        </Wrapper>
      </WidgetBase>
      <PostForm isEditable={isEditable} />
      <PostViewer isEditable={isEditable} />
    </>
  );
};
